import { useCallback, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { subscribeToTenant } from '../services/conversationHub';
import { DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE, getConversationById } from '../services/conversationService';
import { Conversation, Message } from '../types/Conversation';
import { NavSection } from '../types/conversationNav';
import { idsEqual, mergeMessages, upsertAndSortMessages } from '../utils/messageUtils';
import { upsertConversationListItem } from '../utils/conversationListRealtime';
import { applyCachedMessagesIfOmitted } from '../utils/conversationState';
import { removeOptimisticMessages, removeRedundantOptimisticMessages, replaceOptimisticMessages } from '../utils/optimisticMessages';
import { useConversationRealtimeSubscription } from './useConversationRealtimeSubscription';
import type { AiThinkingStepEvent } from './useConversationRealtimeSubscription';
import { useToast } from '../../notifications';

type TenantNotificationHandler = NonNullable<Parameters<typeof subscribeToTenant>[0]['onConversationNotification']>;
type ShowToastHandler = ReturnType<typeof useToast>['showToast'];

interface UseConversationRealtimeStateOptions {
  tenantId?: string;
  navSection: NavSection;
  /**
   * The thread the user is on. Realtime follows this rather than the loaded snapshot, which lags
   * behind while the next thread is still being fetched.
   */
  selectedId: string | null;
  selectedConv: Conversation | null;
  setSelectedConv: Dispatch<SetStateAction<Conversation | null>>;
  setList: Dispatch<SetStateAction<Conversation[]>>;
  setHiddenList: Dispatch<SetStateAction<Conversation[]>>;
  loadConversationLists: () => Promise<void>;
  clearSelectionForConversation: (conversationId: string) => void;
  handleConversationNotification: TenantNotificationHandler;
  onListItemUnreadCounts?: (conversationId: string, unreadCountByParticipant: Record<string, number>) => void;
  onAiThinkingStep?: (event: AiThinkingStepEvent) => void;
  onAiThinkingCompleted?: () => void;
  showToast: ShowToastHandler;
}

interface UseConversationRealtimeStateResult {
  onMessagesCommitted: (conversationId: string, sentMessages: Message[]) => void;
  onMessagesReconciled: (conversationId: string, replacements: Array<{ optimisticId: string; message: Message }>) => void;
  onMessagesRemoved: (conversationId: string, messageIds: string[]) => void;
}

export const useConversationRealtimeState = ({
  tenantId,
  navSection,
  selectedId,
  selectedConv,
  setSelectedConv,
  setList,
  setHiddenList,
  loadConversationLists,
  clearSelectionForConversation,
  handleConversationNotification,
  onListItemUnreadCounts,
  onAiThinkingStep,
  onAiThinkingCompleted,
  showToast,
}: UseConversationRealtimeStateOptions): UseConversationRealtimeStateResult => {
  // Deliberately not selectedConv?.id: while switching threads the snapshot still holds the
  // previous conversation, and following it would let that thread's realtime updates and
  // reconnect refresh overwrite the pane, dragging the user back to the thread they just left.
  const realtimeConversationId = selectedId ?? undefined;

  // Only reuse the loaded snapshot when it actually belongs to the thread realtime is following.
  const loadedSnapshot = selectedConv && idsEqual(selectedConv.id, realtimeConversationId)
    ? selectedConv
    : null;

  useEffect(() => {
    if (!tenantId) return;

    let disposed = false;
    let unsubscribeTenant: (() => Promise<void>) | null = null;

    const attachTenant = async () => {
      try {
        unsubscribeTenant = await subscribeToTenant({
          tenantId,
          onConversationListItemUpdated: (event) => {
            if (disposed) return;

            onListItemUnreadCounts?.(event.conversationId, event.item.unreadCountByParticipant);

            const listItemPayload = {
              conversationId: event.conversationId,
              item: event.item,
            };

            const itemIsSpam = Boolean(event.item.isSpam ?? event.item.isHidden);
            if (itemIsSpam) {
              setList(current => current.filter(conversation => !idsEqual(conversation.id, event.conversationId)));
              setHiddenList(current => upsertConversationListItem(current, listItemPayload));

              if (navSection !== 'spam') {
                clearSelectionForConversation(event.conversationId);
              }
              return;
            }

            setHiddenList(current => current.filter(conversation => !idsEqual(conversation.id, event.conversationId)));
            if (navSection === 'spam') {
              clearSelectionForConversation(event.conversationId);
            }

            setList(current => upsertConversationListItem(current, listItemPayload));
          },
          onConversationNotification: (event) => {
            if (disposed) return;
            void handleConversationNotification(event);
          },
          onReconnected: async () => {
            if (!disposed) {
              await loadConversationLists();
            }
          },
        });
      } catch (error) {
        if (!disposed) {
          const message = error instanceof Error ? error.message : 'Realtime list sync unavailable';
          console.warn('[conversations] tenant/list realtime unavailable, detail realtime can still work', message);
          showToast('Conversation list realtime unavailable (message realtime still active)', 'info');
        }
      }
    };

    void attachTenant();

    return () => {
      disposed = true;
      if (unsubscribeTenant) {
        void unsubscribeTenant();
      }
    };
  }, [clearSelectionForConversation, handleConversationNotification, loadConversationLists, navSection, onListItemUnreadCounts, setHiddenList, setList, showToast, tenantId]);

  const onMessagesCommitted = useCallback((conversationId: string, sentMessages: Message[]) => {
    if (sentMessages.length === 0) return;

    const lastTimestamp = sentMessages[sentMessages.length - 1].timestamp;
    setSelectedConv(current => {
      if (!current || current.id !== conversationId) return current;
      return {
        ...current,
        messages: sentMessages.reduce((messages, message) => upsertAndSortMessages(messages, message), current.messages),
        updatedAt: lastTimestamp,
      };
    });

    setList(current => current.map(conversation => conversation.id === conversationId ? { ...conversation, updatedAt: lastTimestamp } : conversation));
    setHiddenList(current => current.map(conversation => conversation.id === conversationId ? { ...conversation, updatedAt: lastTimestamp } : conversation));
  }, [setHiddenList, setList, setSelectedConv]);

  const onMessagesReconciled = useCallback((conversationId: string, replacements: Array<{ optimisticId: string; message: Message }>) => {
    if (replacements.length === 0) return;

    const lastTimestamp = replacements[replacements.length - 1].message.timestamp;
    setSelectedConv(current => {
      if (!current || current.id !== conversationId) return current;
      return {
        ...current,
        messages: replaceOptimisticMessages(current.messages, replacements),
        updatedAt: lastTimestamp,
      };
    });

    setList(current => current.map(conversation => conversation.id === conversationId ? { ...conversation, updatedAt: lastTimestamp } : conversation));
    setHiddenList(current => current.map(conversation => conversation.id === conversationId ? { ...conversation, updatedAt: lastTimestamp } : conversation));
  }, [setHiddenList, setList, setSelectedConv]);

  const onMessagesRemoved = useCallback((conversationId: string, messageIds: string[]) => {
    if (messageIds.length === 0) return;

    setSelectedConv(current => {
      if (!current || current.id !== conversationId) return current;
      return {
        ...current,
        messages: removeOptimisticMessages(current.messages, messageIds),
      };
    });
  }, [setSelectedConv]);

  const handleRealtimeMessageCreated = useCallback((event: {
    conversationId: string;
    message: Message;
    timestamp: string;
  }) => {
    const isActiveConversation = idsEqual(event.conversationId, realtimeConversationId);

    if (isActiveConversation && event.message.sender.type === 'aiAssistant') {
      onAiThinkingCompleted?.();
    }

    if (isActiveConversation) {
      setSelectedConv(current => {
        if (!current || !idsEqual(current.id, event.conversationId)) return current;
        return {
          ...current,
          messages: upsertAndSortMessages(
            removeRedundantOptimisticMessages(current.messages, event.message),
            event.message,
          ),
          updatedAt: event.timestamp || event.message.timestamp,
        };
      });
    }

    const applyMessageToListItem = (conversation: Conversation): Conversation => {
      if (!idsEqual(conversation.id, event.conversationId)) {
        return conversation;
      }

      return {
        ...conversation,
        messages: upsertAndSortMessages(conversation.messages, event.message),
        updatedAt: event.timestamp || event.message.timestamp,
      };
    };

    setList(current => current.map(applyMessageToListItem));
    setHiddenList(current => current.map(applyMessageToListItem));
  }, [onAiThinkingCompleted, realtimeConversationId, setHiddenList, setList, setSelectedConv]);

  const handleRealtimeConversationUpdated = useCallback((event: {
    conversationId: string;
    conversation: Conversation;
  }) => {
    if (!idsEqual(event.conversationId, realtimeConversationId)) return;

    const mergeConversationSnapshot = (current: Conversation): Conversation => {
      const incomingMessages = event.conversation.messages;
      const nextMessages = incomingMessages.length > 0
        ? mergeMessages(current.messages, incomingMessages)
        : current.messages;

      return {
        ...current,
        title: event.conversation.title,
        participants: event.conversation.participants,
        messages: nextMessages,
        totalMessageCount: incomingMessages.length > 0
          ? Math.max(current.totalMessageCount ?? current.messages.length, event.conversation.totalMessageCount ?? nextMessages.length)
          : current.totalMessageCount,
        hasMoreMessages: incomingMessages.length > 0
          ? event.conversation.hasMoreMessages
          : current.hasMoreMessages,
        updatedAt: event.conversation.updatedAt,
        isSigned: event.conversation.isSigned,
        isAiAssistantMuted: event.conversation.isAiAssistantMuted,
        isFavorite: current.isFavorite,
        isPinned: current.isPinned,
        isSpam: current.isSpam,
        isHidden: current.isHidden,
        note: current.note,
        notes: current.notes,
        visibleNoteCount: current.visibleNoteCount,
        assignedToEmployeeId: event.conversation.assignedToEmployeeId,
        assignedToEmployeeName: event.conversation.assignedToEmployeeName,
        containsWhatsAppMessages: event.conversation.containsWhatsAppMessages,
        containsInstagramMessages: event.conversation.containsInstagramMessages,
        containsInstagramComments: event.conversation.containsInstagramComments,
        containsMessengerComments: event.conversation.containsMessengerComments,
      };
    };

    setSelectedConv(current => {
      if (!current || !idsEqual(current.id, event.conversationId)) return current;
      return mergeConversationSnapshot(current);
    });

    setList(current => current.map(conversation => idsEqual(conversation.id, event.conversationId)
      ? mergeConversationSnapshot(conversation)
      : conversation));

    setHiddenList(current => current.map(conversation => idsEqual(conversation.id, event.conversationId)
      ? mergeConversationSnapshot(conversation)
      : conversation));
  }, [realtimeConversationId, setHiddenList, setList, setSelectedConv]);

  const handleRealtimeAiThinkingStep = useCallback((event: AiThinkingStepEvent) => {
    if (!idsEqual(event.conversationId, realtimeConversationId)) return;
    onAiThinkingStep?.(event);
  }, [onAiThinkingStep, realtimeConversationId]);

  const healAfterReconnect = useCallback(async () => {
    if (!realtimeConversationId) return;

    const refreshed = await getConversationById(realtimeConversationId, {
      includeSpam: navSection === 'spam' || Boolean(loadedSnapshot?.isSpam ?? loadedSnapshot?.isHidden),
      messagePageSize: DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE,
      ifUnchangedSince: loadedSnapshot?.messages.length ? loadedSnapshot.updatedAt : undefined,
    });

    if (!refreshed) return;

    const next = applyCachedMessagesIfOmitted(refreshed, loadedSnapshot) ?? refreshed;
    // The user may have moved on while this request was in flight; adopting it then would pull
    // them back into the previous thread.
    setSelectedConv(current => (current && !idsEqual(current.id, next.id) ? current : next));
    setList(current => current.map(conversation => conversation.id === next.id ? {
      ...conversation,
      title: next.title,
      participants: next.participants,
      updatedAt: next.updatedAt,
      isSigned: next.isSigned,
      isAiAssistantMuted: next.isAiAssistantMuted,
      containsWhatsAppMessages: next.containsWhatsAppMessages,
      containsInstagramMessages: next.containsInstagramMessages,
      containsInstagramComments: next.containsInstagramComments,
      containsMessengerComments: next.containsMessengerComments,
      messages: next.messages,
    } : conversation));
    setHiddenList(current => current.map(conversation => conversation.id === next.id ? {
      ...conversation,
      title: next.title,
      participants: next.participants,
      updatedAt: next.updatedAt,
      isSigned: next.isSigned,
      isAiAssistantMuted: next.isAiAssistantMuted,
      containsWhatsAppMessages: next.containsWhatsAppMessages,
      containsInstagramMessages: next.containsInstagramMessages,
      containsInstagramComments: next.containsInstagramComments,
      containsMessengerComments: next.containsMessengerComments,
      messages: next.messages,
    } : conversation));
  }, [navSection, realtimeConversationId, loadedSnapshot, setHiddenList, setList, setSelectedConv]);

  useConversationRealtimeSubscription({
    enabled: Boolean(realtimeConversationId),
    tenantId,
    conversationId: realtimeConversationId,
    onMessageCreated: handleRealtimeMessageCreated,
    onConversationUpdated: handleRealtimeConversationUpdated,
    onAiThinkingStep: handleRealtimeAiThinkingStep,
    onReconnected: healAfterReconnect,
    onError: (message) => showToast(message, 'error'),
  });

  return {
    onMessagesCommitted,
    onMessagesReconciled,
    onMessagesRemoved,
  };
};